/*
  cutpiece-cart.js
  ----------------------------------------------------------------------------
  ONE shared add-to-cart engine for the custom product sections
  (ioivfb / hihfuivr / new66).

  Why this file exists:
    Each custom product section used to roll its own add-to-cart. One did a
    hand-built AJAX call into its own private drawer, the other two did a plain
    non-AJAX form POST (full page reload, no popup at all). That is why the
    three product layouts behaved differently.

    Everything now goes through window.CutpieceCart, which talks to the SAME
    theme mini-cart (<cart-notification> + #minicart-form) that the rest of the
    theme uses, so quick-add on collection pages, upsells and the product pages
    all stay in sync.

  Usage from a section:
    CutpieceCart.bind(formElement, {
      button:   submitButtonElement,      // optional
      prepare:  function (form) {...},    // optional, runs before FormData is built
      addingText: 'Adding...'             // optional
    });
  ----------------------------------------------------------------------------
*/
(function () {
  'use strict';

  if (window.CutpieceCart) return;

  /* ---------------------------------------------------------------- helpers */

  function shopRoot() {
    var r =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return r.replace(/\/$/, '');
  }

  function cartAddUrl() {
    return (
      (window.routes && window.routes.cart_add_url) || shopRoot() + '/cart/add'
    );
  }

  // The theme's mini-cart custom element. Present on every page except /cart.
  function themeCart() {
    return (
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer')
    );
  }

  function onCartPage() {
    return document.body.classList.contains('template-cart');
  }

  /* ------------------------------------------------- header badges / totals */

  function paintBadges(cart) {
    if (!cart || cart.item_count === undefined) return;

    document.querySelectorAll('.cart-count').forEach(function (el) {
      if (el.classList.contains('cart-count-drawer')) {
        el.innerHTML = '(' + cart.item_count + ')';
      } else {
        el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
      }
    });

    var headerTotal = document.querySelector('header-total-price');
    if (headerTotal && typeof headerTotal.updateTotal === 'function') {
      try {
        headerTotal.updateTotal(cart);
      } catch (e) {}
    }

    var freeShip = document.querySelector('free-ship-progress-bar');
    if (freeShip && typeof freeShip.init === 'function') {
      try {
        freeShip.init(cart.items_subtotal_price);
      } catch (e) {}
    }
  }

  /* ----------------------------------------------- mini-cart markup repaint */

  // Swap the contents of the theme mini-cart with freshly rendered markup.
  // `markup` is the rendered 'minicart-form' section as a string.
  function paintMiniCart(markup) {
    var target = document.getElementById('minicart-form');
    if (!target || !markup) return false;

    var doc = new DOMParser().parseFromString(markup, 'text/html');
    var fresh = doc.querySelector('#minicart-form');
    if (!fresh) return false;

    target.innerHTML = fresh.innerHTML;

    // The free-shipping bar and the countdown block sit OUTSIDE #minicart-form
    // in the header markup, so they have to be copied across separately or the
    // drawer keeps showing a stale "add X more for free shipping" message.
    var pairs = [
      ['.cart-countdown-time', '.cart-countdown-time'],
      ['free-ship-progress-bar', 'free-ship-progress-bar'],
    ];
    pairs.forEach(function (pair) {
      var current = document.querySelector(pair[0]);
      var next = doc.querySelector(pair[1]);
      if (current && next) current.innerHTML = next.innerHTML;
    });

    var cart = themeCart();
    if (cart) {
      cart.classList.remove('is-empty');
      // Re-attach listeners to the markup we just injected (close buttons,
      // gift wrap, addons, ...). Without this the new nodes are inert.
      if (typeof cart.cartAction === 'function') {
        try {
          cart.cartAction();
        } catch (e) {}
      }
    }
    return true;
  }

  // Fallback used when the /cart/add response did not carry section markup.
  function refetchMiniCart() {
    return fetch(
      window.location.pathname + '?section_id=minicart-form',
      { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
    )
      .then(function (res) {
        return res.text();
      })
      .then(function (text) {
        return paintMiniCart(text);
      })
      .catch(function () {
        return false;
      });
  }

  /* -------------------------------------------------------- cart page state */

  // On /cart there is no mini-cart to open, so refresh the cart section itself.
  function repaintCartPage() {
    var host = document.getElementById('main-cart-items');
    if (!host) return Promise.resolve(false);

    var sectionId = host.dataset.id || 'main-cart-items';
    return fetch(
      window.location.pathname + '?section_id=' + encodeURIComponent(sectionId)
    )
      .then(function (res) {
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var freshHost = doc.querySelector('#main-cart-items') || doc.body;

        var contents = host.querySelector('.js-contents');
        var freshContents = freshHost.querySelector('.js-contents');
        if (contents && freshContents) {
          contents.innerHTML = freshContents.innerHTML;
        } else {
          host.innerHTML = freshHost.innerHTML;
        }

        var totals = document.querySelector('.cart-info .totals');
        var freshTotals = doc.querySelector('.cart-info .totals');
        if (totals && freshTotals) totals.innerHTML = freshTotals.innerHTML;
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  /* ------------------------------------------------------------- open popup */

  function openThemeCart() {
    var cart = themeCart();
    if (!cart || typeof cart.open !== 'function') return;

    // "go_to_cart_page" mode: open() forwards to /cart, which is intended.
    try {
      cart.open();
    } catch (e) {}
  }

  /* ------------------------------------------------------------ user errors */

  function showError(message, scope) {
    if (!message) return;

    // Prefer the theme toast so the styling matches the rest of the theme.
    if (typeof window.showToast === 'function') {
      try {
        var wrap = document.createElement('div');
        wrap.className = 'form__message error inline-flex align-center';
        var span = document.createElement('span');
        span.className = 'ml-5';
        span.textContent = message; // textContent = no HTML injection
        wrap.appendChild(span);
        window.showToast(wrap.outerHTML, 5000, 'modal-error');
        return;
      } catch (e) {}
    }

    var slot = scope && scope.querySelector('[data-cart-error]');
    if (slot) {
      slot.textContent = message;
      slot.hidden = false;
      return;
    }
    console.error('[cart] ' + message);
  }

  /* ------------------------------------------------------------------- core */

  var inFlight = false;

  function addToCart(form, opts) {
    opts = opts || {};
    if (inFlight) return Promise.resolve();
    if (!form) {
      console.error('[cart] no form supplied to CutpieceCart.add');
      return Promise.resolve();
    }

    var button =
      opts.button ||
      form.querySelector('[type="submit"][name="add"]') ||
      form.querySelector('[type="submit"]');

    if (typeof opts.prepare === 'function') {
      try {
        opts.prepare(form);
      } catch (e) {
        console.error('[cart] prepare() failed', e);
      }
    }

    var formData = new FormData(form);

    // Guard against a missing/blank variant id, which Shopify answers with a
    // 404 that used to surface as a silent no-op.
    var variantId = formData.get('id');
    if (!variantId) {
      showError('Please choose an option before adding to cart.', form);
      return Promise.resolve();
    }

    // Ask Shopify to render the mini-cart in the SAME round trip. This is what
    // keeps the popup in step with the cart without a page refresh.
    var cart = themeCart();
    if (cart && typeof cart.getSectionsToRender === 'function' && !onCartPage()) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (s) {
          return s.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
    }

    inFlight = true;
    var originalLabel = null;
    if (button) {
      originalLabel = button.textContent;
      button.disabled = true;
      button.classList.add('loading');
      if (opts.addingText !== false) {
        button.textContent = opts.addingText || 'Adding...';
      }
    }

    return fetch(cartAddUrl(), {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        // Shopify reports add-to-cart failures as {status, message, description}
        if (!result.ok || result.data.status) {
          throw new Error(
            result.data.description ||
              result.data.message ||
              'Could not add this item to your cart.'
          );
        }

        var data = result.data;

        // Repaint the right surface: the cart page has no popup to open.
        var repaint = onCartPage()
          ? repaintCartPage()
          : data.sections && paintMiniCart(data.sections['minicart-form'])
          ? Promise.resolve(true)
          : refetchMiniCart();

        return repaint
          .then(function () {
            // Authoritative counts/totals straight from the cart, on every
            // template including /cart - the header bubble has to move there too.
            return fetch(shopRoot() + '/cart.js', {
              headers: { Accept: 'application/json' },
            })
              .then(function (r) {
                return r.json();
              })
              .then(paintBadges)
              .catch(function () {});
          })
          .then(function () {
            if (!onCartPage()) openThemeCart();
            document.dispatchEvent(
              new CustomEvent('cart:updated', { detail: data })
            );
            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
              try {
                window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'cutpiece-cart',
                  productVariantId: variantId,
                  cartData: data,
                });
              } catch (e) {}
            }
          });
      })
      .catch(function (err) {
        showError(err && err.message ? err.message : String(err), form);
      })
      .finally(function () {
        inFlight = false;
        if (button) {
          button.disabled = false;
          button.classList.remove('loading');
          if (originalLabel !== null && opts.addingText !== false) {
            button.textContent = originalLabel;
          }
        }
      });
  }

  /* ------------------------------------------------------------------- bind */

  function bind(form, opts) {
    if (!form || form.dataset.cutpieceBound === 'true') return;
    form.dataset.cutpieceBound = 'true';

    form.addEventListener('submit', function (event) {
      // Let Shop Pay / dynamic checkout buttons do their own thing.
      var submitter = event.submitter;
      if (
        submitter &&
        (submitter.classList.contains('shopify-payment-button__button') ||
          submitter.closest('.shopify-payment-button'))
      ) {
        return;
      }
      event.preventDefault();
      addToCart(form, opts);
    });
  }

  window.CutpieceCart = {
    bind: bind,
    add: addToCart,
    paintBadges: paintBadges,
    paintMiniCart: paintMiniCart,
    refetchMiniCart: refetchMiniCart,
    open: openThemeCart,
  };
})();
